/**
 * Servicio de Encriptación
 * Monedero Digital NFC
 * 
 * Maneja toda la encriptación/desencriptación de datos sensibles
 */

import CryptoJS from 'crypto-js';
import { APP_CONFIG, ERROR_TYPES } from '../utils/constants';

class EncryptionService {
  constructor() {
    this.secretKey = APP_CONFIG.SECRET_KEY;
    
    if (!this.secretKey) {
      console.warn('⚠️ SECRET_KEY no configurada. La encriptación no estará disponible.');
    }
  }

  /**
   * Encripta datos usando AES-256
   */
  encrypt(data) {
    try {
      if (!this.secretKey) {
        throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
      }

      const dataString = typeof data === 'string' ? data : JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(dataString, this.secretKey).toString();
      
      return encrypted;
    } catch (error) {
      console.error('Error encriptando datos:', error);
      throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
    }
  }

  /**
   * Desencripta datos previamente encriptados
   */
  decrypt(encryptedData) {
    try {
      if (!this.secretKey) {
        throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
      }

      if (!encryptedData) {
        return null;
      }

      const bytes = CryptoJS.AES.decrypt(encryptedData, this.secretKey);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decryptedString) {
        throw new Error(ERROR_TYPES.DATA_CORRUPTED);
      }

      // Intenta parsear como JSON, si falla retorna el string
      try {
        return JSON.parse(decryptedString);
      } catch {
        return decryptedString;
      }
    } catch (error) {
      console.error('Error desencriptando datos:', error);
      throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
    }
  }

  /**
   * Genera un hash SHA-256 de datos
   */
  hash(data) {
    try {
      const dataString = typeof data === 'string' ? data : JSON.stringify(data);
      return CryptoJS.SHA256(dataString).toString();
    } catch (error) {
      console.error('Error generando hash:', error);
      return null;
    }
  }

  /**
   * Verifica si un hash coincide con los datos
   */
  verifyHash(data, hash) {
    const generatedHash = this.hash(data);
    return generatedHash === hash;
  }

  /**
   * Genera un hash de contraseña (PBKDF2)
   */
  hashPassword(password) {
    try {
      const salt = CryptoJS.lib.WordArray.random(128/8).toString();
      const hash = CryptoJS.PBKDF2(password, salt, {
        keySize: 256/32,
        iterations: 1000
      }).toString();
      
      return `${salt}:${hash}`;
    } catch (error) {
      console.error('Error hasheando contraseña:', error);
      throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
    }
  }

  /**
   * Verifica una contraseña contra su hash
   */
  verifyPassword(password, storedHash) {
    try {
      const [salt, hash] = storedHash.split(':');
      const hashToVerify = CryptoJS.PBKDF2(password, salt, {
        keySize: 256/32,
        iterations: 1000
      }).toString();
      
      return hash === hashToVerify;
    } catch (error) {
      console.error('Error verificando contraseña:', error);
      return false;
    }
  }

  /**
   * Encripta datos para almacenar en tarjeta NFC
   */
  encryptForNFC(studentData) {
    try {
      // Estructura de datos para NFC
      const nfcData = {
        estudianteId: studentData.estudianteId || studentData.id,
        saldo: studentData.saldo || studentData.saldoActual || 0,
        ultimaActualizacion: new Date().toISOString(),
        version: '1.0'
      };

      // Genera hash de integridad
      const dataHash = this.hash(nfcData);
      nfcData.hash = dataHash;

      // Encripta todo
      return this.encrypt(nfcData);
    } catch (error) {
      console.error('Error encriptando para NFC:', error);
      throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
    }
  }

  /**
   * Desencripta y valida datos de tarjeta NFC
   */
  decryptFromNFC(encryptedData) {
    try {
      const decrypted = this.decrypt(encryptedData);
      
      if (!decrypted || !decrypted.hash) {
        throw new Error(ERROR_TYPES.DATA_CORRUPTED);
      }

      // Extrae el hash antes de validar
      const { hash, ...data } = decrypted;

      // Verifica integridad
      if (!this.verifyHash(data, hash)) {
        throw new Error(ERROR_TYPES.DATA_CORRUPTED);
      }

      return data;
    } catch (error) {
      console.error('Error desencriptando desde NFC:', error);
      throw new Error(ERROR_TYPES.NFC_READ_ERROR);
    }
  }

  /**
   * Genera un token de sesión
   */
  generateSessionToken(userData) {
    try {
      const tokenData = {
        userId: userData.id,
        username: userData.username,
        rol: userData.rol,
        timestamp: Date.now(),
        expiresAt: Date.now() + (30 * 60 * 1000) // 30 minutos
      };

      return this.encrypt(tokenData);
    } catch (error) {
      console.error('Error generando token:', error);
      throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
    }
  }

  /**
   * Valida un token de sesión
   */
  validateSessionToken(token) {
    try {
      const tokenData = this.decrypt(token);
      
      if (!tokenData || !tokenData.expiresAt) {
        return { valid: false, expired: true };
      }

      const isExpired = Date.now() > tokenData.expiresAt;
      
      return {
        valid: !isExpired,
        expired: isExpired,
        data: tokenData
      };
    } catch (error) {
      console.error('Error validando token:', error);
      return { valid: false, expired: true };
    }
  }

  /**
   * Encripta una transacción completa
   */
  encryptTransaction(transaction) {
    try {
      // Crea una copia para no modificar el original
      const txCopy = { ...transaction };
      
      // Genera hash de la transacción
      const txHash = this.hash(txCopy);
      txCopy.hash = txHash;

      // Encripta
      const encrypted = this.encrypt(txCopy);
      
      return {
        ...transaction,
        encriptado: encrypted,
        hash: txHash
      };
    } catch (error) {
      console.error('Error encriptando transacción:', error);
      throw new Error(ERROR_TYPES.ENCRYPTION_ERROR);
    }
  }

  /**
   * Desencripta y valida una transacción
   */
  decryptTransaction(encryptedTransaction) {
    try {
      if (!encryptedTransaction.encriptado) {
        // Si no está encriptada, retorna tal cual
        return encryptedTransaction;
      }

      const decrypted = this.decrypt(encryptedTransaction.encriptado);
      
      // Valida hash si existe
      if (decrypted.hash) {
        const { hash, ...data } = decrypted;
        if (!this.verifyHash(data, hash)) {
          console.warn('Hash de transacción no válido');
        }
      }

      return decrypted;
    } catch (error) {
      console.error('Error desencriptando transacción:', error);
      throw new Error(ERROR_TYPES.DATA_CORRUPTED);
    }
  }
}

// Exporta una instancia única (Singleton)
const encryptionService = new EncryptionService();
export default encryptionService;
